import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import './PartnerIntegrationPage.css';

const AnimatedHeading = ({ as: Tag, className, children, headingRef }) => {
  const text = typeof children === 'string' ? children : '';

  return (
    <Tag ref={headingRef} className={className} aria-label={text}>
      <span className="partner-heading-text" aria-hidden="true">
        {text.split('').map((char, index) => (
          <span
            key={`${text}-${index}`}
            className={`partner-heading-letter${
              char === ' ' ? ' partner-heading-letter-space' : ''
            }`}
            style={{ '--letter-index': index }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </Tag>
  );
};

const PartnerIntegrationPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const backPath =
    servicesIndexView === 'hr' ||
    location.pathname.startsWith('/services/hr/')
      ? '/services/hr'
      : servicesIndexView
        ? '/services'
        : '/';

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, 80);

    return () => window.clearTimeout(timer);
  }, []);

  const breadcrumbItems = buildServiceBreadcrumbs('hr', 'Partner Integration');
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <AnimatedHeading
            as="h1"
            className={`partner-main-heading partner-heading-hero${
              isVisible ? ' is-visible' : ''
            }`}
          >
            Partner Integration Services
          </AnimatedHeading>
          <p>
            BlackRoth Partner Integration Services enable seamless connectivity
            between HR platforms, payroll systems, and third-party business
            applications.
          </p>
        </div>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>
            Our Partner Integration Services are designed to simplify complex
            system interactions by securely connecting BlackRoth HR solutions
            with trusted external platforms.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Supported Integrations</h2>
          <p>
            BlackRoth supports integrations with payroll providers, accounting
            systems, learning platforms, recruitment tools, and compliance
            solutions to ensure smooth data flow across systems.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Secure & Scalable APIs</h2>
          <p>
            Our APIs are built using industry standards, ensuring data security,
            reliability, and scalability while supporting future business
            growth.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Custom Partner Enablement</h2>
          <p>
            We offer tailored integration strategies for enterprise partners,
            aligning technology with business workflows and operational goals.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PartnerIntegrationPage;
